/**
 * What a row-delete response actually tells us about the rows we sent.
 *
 * Small and pure on purpose. The decision it makes is the one that decides
 * whether a staged delete mark is cleared or kept, and a mark kept for a row
 * that is already gone becomes, at the next Save, a DELETE aimed at whatever
 * row the database hands that rowid to next. Living inside a 5,000-line
 * component, that branch had no way to be tested; here it does.
 */

/** Row ids as they travel between the grid and the server. SQLite uses
 *  numbers; Firebird's `__rowid` is a JSON-encoded PK tuple, i.e. a string.
 *  Narrowing this to `number[]` would make `new Set<number>(gone)` look
 *  correct while purging nothing. */
export type RowId = number | string

export interface RowDeleteResponse {
  ok: boolean
  error?: string
  /** How many rows went. An explicit `null` means the backend cannot say —
   *  not the same as the field being absent, which means an older backend
   *  that never reported it. */
  deleted?: number | null
  /** WHICH rows went, or null/absent when the backend could not say. */
  deletedIds?: RowId[] | null
}

export type RowDeleteOutcome =
  /** Every row we sent is gone. Safe to clear all their marks. */
  | { kind: 'all' }
  /** These rows went; the rest did not. Clear exactly `gone`. */
  | { kind: 'partial'; gone: RowId[]; sent: number }
  /**
   * Fewer rows went than we sent, and the backend did not say which.
   *
   * Clearing nothing is the only safe move: clearing all would drop the mark
   * for a row still sitting in the table, and the user would believe they had
   * deleted it. The grid reloads and the user can see what is left.
   */
  | { kind: 'short-unknown'; deleted: number; sent: number }
  /**
   * The backend said outright that it CANNOT tell — neither which rows went
   * nor how many.
   *
   * Not the same as a response that simply predates `deletedIds`, and the
   * difference is the whole point. An old server omits the field; this one
   * sends `deleted: null`, which is an answer: "I ran the deletes, I cannot
   * report on them." Folding the two together turned that into "everything
   * went", cleared every mark, and left a row still in the table with the
   * user believing they had deleted it — worse than the behaviour this
   * replaced, which at least said "Deleted 1 of 3".
   */
  | { kind: 'unknown'; sent: number }

export function classifyRowDelete(res: RowDeleteResponse, sent: readonly RowId[]): RowDeleteOutcome {
  if (res.deletedIds != null) {
    // MEMBERSHIP, not length. A backend that also reports cascade-deleted
    // child rows can return more ids than were sent while still missing one
    // of them: `[1, 2, 10, 11]` for `[1, 2, 3]` is four ids, and a length
    // test reads that as "all three went" and clears row 3's mark — for a row
    // still sitting in the table.
    const went = new Set<RowId>(res.deletedIds)
    const missed = sent.filter(id => !went.has(id))
    if (missed.length === 0) return { kind: 'all' }
    // Only the ids we actually asked about. Handing a cascade's child ids to
    // `purge` would clear marks the user never put there — harmless today,
    // but it is not this function's business to invent them.
    return { kind: 'partial', gone: sent.filter(id => went.has(id)), sent: sent.length }
  }
  // No per-row answer. The COUNT is still worth reading — it is what this
  // code had before `deletedIds` existed, and dropping it when the new field
  // is absent turned a detected partial delete into a silent one. A rolling
  // deploy serving the old response shape is enough to get here.
  if (res.deleted != null && res.deleted < sent.length) {
    return { kind: 'short-unknown', deleted: res.deleted, sent: sent.length }
  }
  // `=== null`, not `== null` and not `'deleted' in res`. Over JSON a field
  // is either an explicit null or absent — `undefined` cannot survive the
  // wire — so null IS the backend declining to answer and absent is a
  // backend that never reported it. The looser checks caught a locally
  // constructed `{deleted: undefined}` as well, which is neither.
  if (res.deleted === null) return { kind: 'unknown', sent: sent.length }
  return { kind: 'all' }
}

/**
 * What to tell the user about a delete whose result arrived after the grid had
 * already moved on.
 *
 * This is the only case where a delete can succeed with no evidence on screen:
 * everywhere else the rows simply vanish from the grid in front of the user.
 * So it is also the only one that has to say a plain "it worked" — and the only
 * one that says it about a table nobody can look at, which is why what counts
 * as "it worked" is narrower here than elsewhere.
 *
 * It does NOT take "has the table actually changed". An earlier version did,
 * on the grounds that the epoch also moves on a file or version change, where
 * the same table would still be on screen. Where this runs it cannot: a grid
 * showing a VERSION is forced out of edit mode (`activeMode` in
 * DatabaseViewer), so nothing can be staged and this path is unreachable
 * there; and `fileId` is fixed for a mount's lifetime. So the epoch moving
 * means the TABLE moved.
 *
 * Asking again by name also reintroduced the hazard the epoch was made a
 * number to avoid — rename the new table to the old one's name and the
 * comparison says you never left.
 */
export function staleDeleteMessage(
  res: RowDeleteResponse,
  sent: readonly RowId[],
  table: string,
): { tone: 'success' | 'error'; text: string } {
  const outcome = classifyRowDelete(res, sent)
  const one = sent.length === 1
  const rows = `${sent.length} row${one ? '' : 's'}`
  /**
   * NOTHING went, which two of the branches below can both mean. A DELETE that
   * affects zero rows did not match: the row was not there. So this says that,
   * and says it the same way whichever field carried the answer — the first
   * attempt hedged it ("may still be there") on one branch and asserted it on
   * the other, about the identical fact, and the hedge would have sent the user
   * back to hunt for a row that is already gone.
   */
  const noneWent = one
    ? `The row you marked in "${table}" was already gone.`
    : `None of the ${rows} you marked in "${table}" were there any more.`
  switch (outcome.kind) {
    // Branch on how many rows WENT, not on how many were sent. Keying on the
    // count sent is what produced "Deleted 0 of 1 rows", then — fixing only the
    // number — "which of the 1 row were deleted", and it left "Deleted 0 of 2
    // rows — check which are left" standing, which asks the user to work out
    // which of two rows survived when the answer is "both".
    case 'partial':
      return {
        tone: 'error',
        text: outcome.gone.length === 0
          ? noneWent
          : `Deleted ${outcome.gone.length} of ${rows} in "${table}" — the rest were already gone.`,
      }
    case 'short-unknown':
      // With one row this can only be `deleted: 0` (it is `deleted < sent`), so
      // the singular form never reaches the second sentence.
      return {
        tone: 'error',
        text: outcome.deleted === 0
          ? noneWent
          : `Deleted ${outcome.deleted} of ${rows} in "${table}" — check which are left.`,
      }
    case 'unknown':
      return {
        tone: 'error',
        text: one
          ? `Could not confirm whether the row you marked in "${table}" was deleted — check that table.`
          : `Could not confirm which of the ${rows} were deleted in "${table}" — check that table.`,
      }
    case 'all':
      // `'all'` is also what an answer with NO fields at all classifies as — a
      // rolling deploy serving the pre-`deletedIds` shape reaches it, and so
      // does any backend that stops reporting. That is optimism, and it was
      // only ever internal: it cleared marks, and the user could look at the
      // table. Said out loud, about a table they are no longer on, it becomes
      // a claim nobody checked. Only a per-row answer earns the flat wording.
      if (res.deletedIds == null) {
        return { tone: 'error', text: `Deleted rows in "${table}", but the server did not confirm which — check that table.` }
      }
      return { tone: 'success', text: `Deleted ${rows} in "${table}"` }
  }
}
